/**
 * Gmail API provider — replaces IMAP for Gmail accounts.
 * Uses REST API for fast, reliable sync without connection limit issues.
 *
 * SOURCE OF TRUTH: this file is the single canonical Gmail provider for
 * mailx. Both desktop (mailx-imap, Node) and Android (mailx-store-web,
 * WebView) re-export from here. Earlier they had parallel copies that
 * silently drifted (the 403-quota retry was added to desktop one day and
 * missed in the web copy until a user reported broken prefetch).
 *
 * Platform requirements: globalThis.fetch (Node 18+ and all browsers/WebViews),
 * atob, Uint8Array, TextDecoder. No Node-specific imports.
 */

import type { MailProvider, ProviderFolder, ProviderMessage, FetchOptions } from "./types.js";

const API = "https://gmail.googleapis.com/gmail/v1/users/me";

/** Convert Gmail hex ID to integer UID (lower 48 bits — deterministic, stable) */
function idToUid(id: string): number {
    const hex = id.length > 12 ? id.slice(-12) : id;
    return parseInt(hex, 16);
}

/** Map Gmail label to IMAP-style specialUse */
function labelSpecialUse(label: { id: string; type: string; name: string }): string {
    switch (label.id) {
        case "INBOX": return "inbox";
        case "SENT": return "sent";
        case "DRAFT": return "drafts";
        case "TRASH": return "trash";
        case "SPAM": return "junk";
        case "STARRED": return "";
        case "IMPORTANT": return "";
        default: return "";
    }
}

/** Parse RFC 2822 headers from Gmail metadata payload */
function getHeader(headers: { name: string; value: string }[], name: string): string {
    return headers.find(h => h.name.toLowerCase() === name.toLowerCase())?.value || "";
}

/** Parse "Name <addr>" or "addr" into { name, address } */
function parseAddress(raw: string): { name?: string; address?: string } {
    const match = raw.match(/^"?([^"<]*?)"?\s*<([^>]+)>/);
    if (match) return { name: match[1].trim(), address: match[2].trim() };
    return { address: raw.trim() };
}

function parseAddressList(raw: string): { name?: string; address?: string }[] {
    if (!raw) return [];
    // Split on commas that aren't inside quotes
    return raw.split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/).map(s => parseAddress(s.trim())).filter(a => a.address);
}

/** Rate-limit + cooldown state shared across all GmailApiProvider instances.
 *  Gmail enforces rate limits per Google user, not per JS object, so sharing
 *  state here prevents new `new GmailApiProvider(...)` from side-stepping the
 *  cooldown by creating a fresh instance. Single-account setups won't notice;
 *  multi-account setups are slightly over-throttled, which is safer than
 *  burning through Google's tolerance. */
const rateState = {
    tokens: 0,
    lastRefill: 0,
    cooldownUntil: 0,
    /** Tokens refilled per second (keeps us well under per-user-per-sec caps). */
    rate: 8,
    /** Max burst above steady state. */
    burst: 8,
};
rateState.tokens = rateState.burst;
rateState.lastRefill = Date.now();

/** Extract Google's structured "reason" from a JSON error body when possible
 *  (e.g. `userRateLimitExceeded`, `rateLimitExceeded`, `quotaExceeded`). */
function parseGoogleReason(body: string): string {
    const m = body.match(/"reason"\s*:\s*"([^"]+)"/);
    return m ? m[1] : "";
}

export class GmailApiProvider implements MailProvider {
    constructor(private tokenProvider: () => Promise<string>) {}

    /** Block until (a) cooldown has elapsed and (b) a token is available.
     *  Token-bucket refill happens lazily on each call. */
    private async acquireToken(): Promise<void> {
        // Respect an active cooldown first — refuse to even try until it ends.
        const now = Date.now();
        if (rateState.cooldownUntil > now) {
            const waitMs = rateState.cooldownUntil - now;
            console.log(`  [gmail] cooldown: waiting ${(waitMs / 1000).toFixed(1)}s before next request`);
            await new Promise(r => setTimeout(r, waitMs));
        }
        // Lazy refill, then spin-wait in chunks until a token is ready.
        while (true) {
            const t = Date.now();
            const elapsedSec = (t - rateState.lastRefill) / 1000;
            rateState.tokens = Math.min(rateState.burst, rateState.tokens + elapsedSec * rateState.rate);
            rateState.lastRefill = t;
            if (rateState.tokens >= 1) {
                rateState.tokens -= 1;
                return;
            }
            const waitMs = Math.max(20, ((1 - rateState.tokens) / rateState.rate) * 1000);
            await new Promise(r => setTimeout(r, waitMs));
        }
    }

    private async fetch(path: string, options: RequestInit = {}): Promise<any> {
        const token = await this.tokenProvider();
        const maxAttempts = 6;
        const baseDelayMs = 1000;
        const maxDelayMs = 60_000;
        let lastStatus = 0;
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await this.acquireToken();
            const res = await globalThis.fetch(`${API}${path}`, {
                ...options,
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json",
                    ...options.headers,
                },
            });
            // 403 with "Quota exceeded" body is Google's per-minute quota limit.
            // Not 429. Retry like a rate-limit, don't throw as a permanent failure.
            let isQuota403 = false;
            let reason = "";
            if (res.status === 403 || res.status === 429) {
                try {
                    const cloned = res.clone();
                    const body = await cloned.text();
                    reason = parseGoogleReason(body);
                    if (res.status === 403 && /quota exceeded|rate ?limit|user[-_ ]rate/i.test(body)) isQuota403 = true;
                } catch { /* ignore */ }
            }
            if (res.status === 429 || res.status >= 500 || isQuota403) {
                lastStatus = res.status;
                // Honor Retry-After if present (seconds or HTTP-date)
                const retryAfter = res.headers.get("Retry-After");
                let delay = baseDelayMs * Math.pow(2, attempt);
                if (retryAfter) {
                    const asInt = parseInt(retryAfter, 10);
                    if (!isNaN(asInt)) delay = asInt * 1000;
                    else {
                        const when = Date.parse(retryAfter);
                        if (!isNaN(when)) delay = Math.max(0, when - Date.now());
                    }
                }
                // Per-minute-quota 403 deserves a minimum 60s wait — the quota
                // resets per minute, so shorter backoff just re-triggers.
                if (isQuota403) delay = Math.max(delay, 60_000);
                // Full jitter to avoid synchronized retries
                delay = Math.min(maxDelayMs, delay);
                delay = Math.floor(delay * (0.5 + Math.random() * 0.5));
                // Install a shared cooldown so *other* in-flight workers wait too,
                // not just this one. Prevents the common failure mode where 5
                // parallel workers each retry independently and keep the
                // per-user QPS pegged above the threshold.
                rateState.cooldownUntil = Math.max(rateState.cooldownUntil, Date.now() + delay);
                const reasonTag = reason ? ` reason=${reason}` : (isQuota403 ? " (per-minute quota)" : "");
                console.log(`  [gmail] ${res.status}${reasonTag} (attempt ${attempt + 1}/${maxAttempts}), waiting ${(delay / 1000).toFixed(1)}s${retryAfter ? ` (Retry-After: ${retryAfter})` : ""}...`);
                await new Promise(r => setTimeout(r, delay));
                continue;
            }
            if (!res.ok) {
                const err = await res.text().catch(() => "");
                throw new Error(`Gmail API ${res.status}: ${err.substring(0, 200)}`);
            }
            return res.json();
        }
        // Exhausted all retries — Google is clearly unhappy. Park every
        // subsequent request behind a long cooldown so the next periodic sync
        // (30s) doesn't immediately fire another 6 retries into the same
        // limit. 5 minutes is a floor, not a ceiling — an incoming Retry-After
        // that's longer will still win via the per-attempt cooldown set above.
        const TERMINAL_COOLDOWN_MS = 5 * 60_000;
        rateState.cooldownUntil = Math.max(rateState.cooldownUntil, Date.now() + TERMINAL_COOLDOWN_MS);
        console.log(`  [gmail] exhausted ${maxAttempts} retries — parking further requests for ${TERMINAL_COOLDOWN_MS / 1000}s`);
        throw new Error(`Gmail API: failed after ${maxAttempts} retries (last status ${lastStatus})`);
    }

    async listFolders(): Promise<ProviderFolder[]> {
        const data = await this.fetch("/labels");
        const labels: any[] = data.labels || [];
        const folders: ProviderFolder[] = [];

        for (const label of labels) {
            // Skip system labels that aren't useful as folders
            if (["UNREAD", "STARRED", "IMPORTANT", "CATEGORY_PERSONAL",
                "CATEGORY_SOCIAL", "CATEGORY_PROMOTIONS", "CATEGORY_UPDATES",
                "CATEGORY_FORUMS", "CHAT"].includes(label.id)) continue;

            const specialUse = labelSpecialUse(label);
            // Map Gmail path separators (/) to IMAP-style
            const path = label.name || label.id;
            const name = path.includes("/") ? path.split("/").pop()! : path;

            folders.push({
                path,
                name,
                delimiter: "/",
                specialUse,
                // No \\Noselect on Gmail labels: the unwanted system labels
                // (UNREAD/STARRED/IMPORTANT/CATEGORY_*/CHAT) are filtered
                // above, leaving INBOX/SENT/DRAFT/TRASH/SPAM and user labels —
                // ALL of which are selectable. The previous \\Noselect tag
                // on system labels caused mailx-imap to skip them entirely,
                // so INBOX never appeared in the folder tree.
                flags: [],
            });
        }
        return folders;
    }

    /** List message IDs matching a query, handling pagination.
     *  IMPORTANT: on any error we throw — do NOT return a partial list, because
     *  callers use this for sync reconciliation and a short list would delete
     *  real messages from the local DB. Returning [] silently caused the
     *  "INBOX empty in mailx" bug when a rate-limit hit mid-pagination. */
    private async listMessageIds(query: string, maxResults = 500): Promise<string[]> {
        // maxResults === 0 → unlimited (for date-bounded queries where the
        // query itself caps the result set).
        const unlimited = maxResults === 0;
        const ids: string[] = [];
        let pageToken = "";
        let truncated = false;
        while (true) {
            const pageSize = unlimited ? 500 : Math.min(maxResults - ids.length, 500);
            const params = new URLSearchParams({ q: query, maxResults: String(pageSize) });
            if (pageToken) params.set("pageToken", pageToken);
            const data = await this.fetch(`/messages?${params}`);
            for (const msg of data.messages || []) {
                ids.push(msg.id);
            }
            if (!data.nextPageToken) break;
            if (!unlimited && ids.length >= maxResults) {
                // Hit the caller's cap but the server has more. Flag it so
                // reconcile-style callers can refuse to treat this as complete.
                truncated = true;
                break;
            }
            pageToken = data.nextPageToken;
        }
        (ids as any)._truncated = truncated;
        return ids;
    }

    /** Batch-fetch message metadata or full content */
    private async batchFetch(ids: string[], options: FetchOptions = {}, onChunk?: (msgs: ProviderMessage[]) => void): Promise<ProviderMessage[]> {
        const all: ProviderMessage[] = [];
        const chunkSize = options.source ? 10 : 50; // Smaller chunks for full bodies
        const format = options.source ? "raw" : "metadata";

        for (let i = 0; i < ids.length; i += chunkSize) {
            const chunk = ids.slice(i, i + chunkSize);
            // Sequential fetches to avoid Gmail 429 rate limits
            const messages: any[] = [];
            for (const id of chunk) {
                const params = new URLSearchParams({ format });
                if (format === "metadata") {
                    for (const h of ["From", "To", "Cc", "Subject", "Message-ID", "Date", "In-Reply-To", "References"]) {
                        params.append("metadataHeaders", h);
                    }
                }
                messages.push(await this.fetch(`/messages/${id}?${params}`));
            }

            const parsed: ProviderMessage[] = messages.map(msg => this.parseMessage(msg, options));
            all.push(...parsed);
            if (onChunk) onChunk(parsed);
        }
        return all;
    }

    /** Parse a Gmail API message response into ProviderMessage */
    private parseMessage(msg: any, options: FetchOptions = {}): ProviderMessage {
        const labels: string[] = msg.labelIds || [];
        const headers = msg.payload?.headers || [];

        let source = "";
        if (options.source && msg.raw) {
            // Gmail returns URL-safe base64 — convert to standard base64 then decode
            const base64 = msg.raw.replace(/-/g, "+").replace(/_/g, "/");
            source = new TextDecoder().decode(Uint8Array.from(atob(base64), c => c.charCodeAt(0)));
        }

        const fromRaw = getHeader(headers, "From");
        const toRaw = getHeader(headers, "To");
        const ccRaw = getHeader(headers, "Cc");
        const dateRaw = getHeader(headers, "Date") || "";
        const subject = getHeader(headers, "Subject") || msg.snippet || "";
        const messageId = getHeader(headers, "Message-ID") || "";
        const inReplyTo = getHeader(headers, "In-Reply-To") || "";
        const referencesRaw = getHeader(headers, "References") || "";
        const references = referencesRaw.trim()
            ? referencesRaw.split(/\s+/).filter(r => r.startsWith("<") && r.endsWith(">"))
            : [];

        // `date` = arrival time (Gmail internalDate) so it matches IMAP's
        // INTERNALDATE semantics; `sentDate` = the Date: header (send time).
        // The list defaults to sentDate so identical messages cluster and a
        // received/sent toggle has a real received value (Bob 2026-07-01).
        const internalMs = msg.internalDate ? new Date(Number(msg.internalDate)) : null;
        const headerMs = dateRaw ? new Date(dateRaw) : null;
        return {
            uid: idToUid(msg.id),
            messageId,
            providerId: msg.id,
            date: internalMs ?? headerMs,
            sentDate: headerMs ?? internalMs ?? undefined,
            subject,
            from: parseAddressList(fromRaw),
            to: parseAddressList(toRaw),
            cc: parseAddressList(ccRaw),
            inReplyTo,
            references,
            seen: !labels.includes("UNREAD"),
            flagged: labels.includes("STARRED"),
            answered: false, // Gmail API doesn't expose this directly
            draft: labels.includes("DRAFT"),
            size: msg.sizeEstimate || 0,
            source,
        };
    }

    async fetchSince(folder: string, sinceUid: number, options: FetchOptions = {}): Promise<ProviderMessage[]> {
        // Gmail message IDs are hash-derived, NOT monotonic — filtering by
        // `uid > sinceUid` silently drops new messages whose hash happens to
        // fall below the high-water mark. Fetch by date (when caller supplied
        // a `since` window) or fall back to the recent-200 page.
        void sinceUid;
        let query = `in:${this.folderToLabel(folder)}`;
        if (options.since) {
            // after: is inclusive at day granularity; filter precisely on the
            // client side since Gmail's search is whole-day.
            query += ` after:${this.formatDate(options.since)}`;
        }
        // When the caller bounded by date, let pagination run — the query
        // is self-limiting. Otherwise cap at the recent 200.
        const cap = options.since ? 0 : 200;
        const ids = await this.listMessageIds(query, cap);
        // Skip IDs already in the local store: the listing above is one cheap
        // call, but fetching each message's metadata is a sequential per-ID GET
        // (~200ms). Without this, a routine sync re-fetched all ~200 recent
        // messages every time (~40s) just to discover nothing was new. With the
        // caller's known-UID set, steady state fetches zero (Bob 2026-06-27).
        const fresh = options.knownUids
            ? ids.filter(id => !options.knownUids!.has(idToUid(id)))
            : ids;
        if (options.knownUids) {
            console.log(`[gmail] fetchSince ${folder}: ${ids.length} listed, ${ids.length - fresh.length} already stored, fetching ${fresh.length}`);
        }
        return this.batchFetch(fresh, options);
    }

    async fetchByDate(folder: string, since: Date, before: Date, options: FetchOptions = {},
        onChunk?: (msgs: ProviderMessage[]) => void): Promise<ProviderMessage[]> {
        const afterDate = this.formatDate(since);
        const beforeDate = this.formatDate(before);
        const query = `in:${this.folderToLabel(folder)} after:${afterDate} before:${beforeDate}`;
        // Date-bounded — let pagination drain the full range instead of stopping at 500.
        const ids = await this.listMessageIds(query, 0);
        return this.batchFetch(ids, options, onChunk);
    }

    async fetchByUids(folder: string, uids: number[], options: FetchOptions = {}): Promise<ProviderMessage[]> {
        // UIDs are derived from Gmail IDs — we'd need a reverse lookup
        // For now, fetch all messages in folder and filter
        const query = `in:${this.folderToLabel(folder)}`;
        const ids = await this.listMessageIds(query);
        const uidSet = new Set(uids);
        const matchingIds = ids.filter(id => uidSet.has(idToUid(id)));
        return this.batchFetch(matchingIds, options);
    }

    /** Bulk-fetch raw bodies for many UIDs in one "folder" (Gmail label).
     *  Lists the label once, builds UID→ID map, then streams bodies through
     *  `onBody`. Uses Gmail's HTTP batch endpoint (up to 100 sub-requests per
     *  round-trip) when available, with a single-request fallback so a batch
     *  protocol blip doesn't starve prefetch entirely.
     *
     *  NOTE: Gmail's model is labels, not folders — a single message can be in
     *  many labels. Treating each label as a folder causes duplicate fetches
     *  across labels. Proper fix tracked as separate TODO ("Gmail label-native
     *  model"). For now we mirror the IMAP folder grouping, accepting duplicate
     *  fetches of multi-labeled messages. */
    async fetchBodiesBatch(
        folder: string,
        uids: number[],
        onBody: (uid: number, source: string) => void,
    ): Promise<void> {
        if (uids.length === 0) return;
        const query = `in:${this.folderToLabel(folder)}`;
        const ids = await this.listMessageIds(query, 10000);
        const uidToId = new Map<number, string>();
        for (const id of ids) uidToId.set(idToUid(id), id);
        const wanted: { uid: number; id: string }[] = [];
        for (const uid of uids) {
            const id = uidToId.get(uid);
            if (id) wanted.push({ uid, id });
        }
        if (wanted.length === 0) return;

        // Item 16 / C24 Gmail half: HTTP /batch prefetch. One multipart POST
        // with up to 100 sub-requests replaces N round-trips. Typical ×10-20
        // speedup on large folders. Each batch still costs the same tokens
        // from the rate bucket as the individual GETs would have (no free
        // ride), but wall-clock latency collapses to roughly one round-trip
        // per 100 messages instead of per message.
        const BATCH_SIZE = 100;
        for (let i = 0; i < wanted.length; i += BATCH_SIZE) {
            const slice = wanted.slice(i, i + BATCH_SIZE);
            try {
                await this.batchFetchBodies(slice, onBody);
            } catch (e: any) {
                // Batch failed (malformed multipart, network reset, auth
                // mid-flight). Fall back to a bounded-concurrency per-message
                // loop for this slice so the user still gets bodies — we'd
                // rather ship the feature degraded than lose prefetch.
                console.error(`  [gmail batch] fell back to per-message: ${e.message}`);
                await this.fetchBodiesIndividually(slice, onBody);
            }
        }
    }

    /** POST /batch/gmail/v1 with up to 100 sub-requests. See
     *  https://developers.google.com/gmail/api/guides/batch
     *  for the multipart/mixed wire format. */
    private async batchFetchBodies(
        items: { uid: number; id: string }[],
        onBody: (uid: number, source: string) => void,
    ): Promise<void> {
        if (items.length === 0) return;
        // One token covers the whole batch endpoint round-trip; Google's
        // per-user quota charges each inner request individually, but the
        // per-second request limit counts the batch as a single request —
        // so rate-bucket budget matches wall-clock cost.
        await this.acquireToken();
        const token = await this.tokenProvider();
        const boundary = `batch_mailx_${Date.now()}_${Math.floor(Math.random() * 1e9)}`;
        const parts: string[] = [];
        for (const { id } of items) {
            parts.push(
                `--${boundary}\r\n` +
                `Content-Type: application/http\r\n` +
                `Content-ID: <${id}>\r\n` +
                `\r\n` +
                `GET /gmail/v1/users/me/messages/${id}?format=raw\r\n` +
                `Accept: application/json\r\n` +
                `\r\n`
            );
        }
        parts.push(`--${boundary}--\r\n`);
        const body = parts.join("");
        const res = await globalThis.fetch("https://gmail.googleapis.com/batch/gmail/v1", {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`,
                "Content-Type": `multipart/mixed; boundary=${boundary}`,
            },
            body,
        });
        if (!res.ok) {
            const text = await res.text().catch(() => "");
            throw new Error(`batch HTTP ${res.status}: ${text.slice(0, 200)}`);
        }
        const respBody = await res.text();
        // Parse multipart — extract each inner application/http response,
        // match back to the Content-ID to recover the Gmail message id,
        // then decode the raw body JSON field.
        const respCt = res.headers.get("content-type") || "";
        const bMatch = respCt.match(/boundary=([^;]+)/i);
        if (!bMatch) throw new Error("batch response missing boundary");
        const respBoundary = bMatch[1].replace(/^"|"$/g, "");
        // Google sometimes replies with its own "batch_..." boundary even if
        // we sent a different one; parsing by the response's boundary is the
        // reliable path. Split on boundary delimiters.
        const chunks = respBody.split(`--${respBoundary}`);
        for (const chunk of chunks) {
            const trimmed = chunk.trim();
            if (!trimmed || trimmed === "--") continue;
            // Each chunk is: outer headers, blank line, inner response
            // (HTTP/1.1 status line, headers, blank line, JSON body).
            // Locate the inner JSON body by finding the double blank after
            // the inner headers section.
            const idMatch = chunk.match(/Content-ID:\s*<?response-<?([^>\s]+)>?/i);
            const gmailId = idMatch ? idMatch[1].replace(/^<|>$/g, "") : "";
            // Skip the outer headers: first blank line ends them.
            const firstBlank = chunk.indexOf("\r\n\r\n");
            if (firstBlank < 0) continue;
            const inner = chunk.slice(firstBlank + 4);
            // Skip the inner HTTP status + headers: second blank line ends them.
            const secondBlank = inner.indexOf("\r\n\r\n");
            if (secondBlank < 0) continue;
            const jsonBody = inner.slice(secondBlank + 4).trim();
            if (!jsonBody || jsonBody.startsWith("--")) continue;
            try {
                const parsed = JSON.parse(jsonBody);
                if (!parsed?.raw) continue;
                const actualId = parsed.id || gmailId;
                if (!actualId) continue;
                const uid = idToUid(actualId);
                const base64 = parsed.raw.replace(/-/g, "+").replace(/_/g, "/");
                const source = new TextDecoder().decode(
                    Uint8Array.from(atob(base64), c => c.charCodeAt(0)),
                );
                onBody(uid, source);
            } catch { /* malformed sub-response — skip, don't poison the batch */ }
        }
    }

    /** Fallback path when batch fails — original bounded-concurrency loop.
     *  Kept on the degraded path so a single bad batch doesn't halt prefetch. */
    private async fetchBodiesIndividually(
        items: { uid: number; id: string }[],
        onBody: (uid: number, source: string) => void,
    ): Promise<void> {
        const CONCURRENCY = 2;
        let cursor = 0;
        const worker = async (): Promise<void> => {
            while (cursor < items.length) {
                const idx = cursor++;
                const { uid, id } = items[idx];
                try {
                    const msg = await this.fetch(`/messages/${id}?format=raw`);
                    if (!msg?.raw) continue;
                    const base64 = msg.raw.replace(/-/g, "+").replace(/_/g, "/");
                    const source = new TextDecoder().decode(
                        Uint8Array.from(atob(base64), c => c.charCodeAt(0)),
                    );
                    onBody(uid, source);
                } catch (e: any) {
                    console.error(`  [gmail batch] UID ${uid}: ${e.message}`);
                }
            }
        };
        await Promise.all(Array.from({ length: Math.min(CONCURRENCY, items.length) }, () => worker()));
    }

    async fetchOne(folder: string, uid: number, options: FetchOptions = {}): Promise<ProviderMessage | null> {
        // Caller (mailx-imap) passes providerId straight from the DB row when
        // available — saves the listMessageIds round trip (up to 2 paged calls
        // of 500 IDs each) plus the UID→ID hash search per body fetch. That's
        // the dominant cost for a stuck "Fetching message body..." spinner
        // when Gmail rate-limits.
        let id = options.providerId;
        if (!id) {
            const query = `in:${this.folderToLabel(folder)}`;
            const ids = await this.listMessageIds(query, 1000);
            id = ids.find(id => idToUid(id) === uid);
            if (!id) return null;
        }
        const format = options.source ? "raw" : "metadata";
        const params = new URLSearchParams({ format });
        if (format === "metadata") {
            for (const h of ["From", "To", "Cc", "Subject", "Message-ID", "Date", "In-Reply-To", "References"]) {
                params.append("metadataHeaders", h);
            }
        }
        const msg = await this.fetch(`/messages/${id}?${params}`);
        return this.parseMessage(msg, options);
    }

    /** Apply the absolute flag state to a message.
     *  Gmail model: flags are labels. `\Seen` is the *absence* of UNREAD;
     *  `\Flagged` is the presence of STARRED. We always send both add and
     *  remove so the end state matches regardless of what was there before,
     *  which makes the call idempotent and safe to retry. */
    async setFlags(folder: string, uid: number, flags: string[], messageId?: string): Promise<void> {
        // Prefer the caller-supplied Gmail message id (stored provider_id).
        // Without it we fall back to a capped list-and-hash search, which
        // misses any message past the most-recent ~1000 — so starring an older
        // message threw "UID not found in INBOX", the flag never reached Gmail,
        // and the local star got reverted on the next sync (Bob 2026-06-12).
        // Same fix already applied to trashMessage.
        let id = messageId;
        if (!id) {
            const query = `in:${this.folderToLabel(folder)}`;
            const ids = await this.listMessageIds(query, 1000);
            id = ids.find(id => idToUid(id) === uid);
        }
        if (!id) throw new Error(`Gmail setFlags: UID ${uid} not found in ${folder}`);
        const flagSet = new Set(flags);
        const addLabelIds: string[] = [];
        const removeLabelIds: string[] = [];
        if (flagSet.has("\\Flagged")) addLabelIds.push("STARRED"); else removeLabelIds.push("STARRED");
        if (flagSet.has("\\Seen")) removeLabelIds.push("UNREAD"); else addLabelIds.push("UNREAD");
        await this.fetch(`/messages/${id}/modify`, {
            method: "POST",
            body: JSON.stringify({ addLabelIds, removeLabelIds }),
        });
    }

    /** Move a message to the trash label. Gmail treats trash as a label, not
     *  as a destination folder — `POST /messages/{id}/trash` is the native
     *  path (equivalent to setting TRASH and removing INBOX in one op).
     *  Used by mailx's delete/trash path. */
    async trashMessage(folder: string, uid: number, gmailId?: string): Promise<void> {
        // Prefer the caller-supplied Gmail message id (mailx stores it as
        // `provider_id`). The fallback — list the folder and hash-match the
        // uid — is capped at 1000, so on a large mailbox it CANNOT find a
        // message past the most-recent ~1000: the trash then throws, the
        // sync action fails its retries, and clearTombstoneForUid resurrects
        // the message ("deletions un-happening", Bob 2026-05-17). Passing the
        // stored id skips that broken search entirely.
        let id = gmailId;
        if (!id) {
            const ids = await this.listMessageIds(`in:${this.folderToLabel(folder)}`, 1000);
            id = ids.find(x => idToUid(x) === uid);
        }
        if (!id) throw new Error(`Gmail trashMessage: UID ${uid} not found in ${folder}`);
        await this.fetch(`/messages/${id}/trash`, { method: "POST" });
    }

    /** Move between "folders" == swap one label for another via modifyLabels.
     *  System labels (INBOX/SENT/TRASH/SPAM) are translated from the folder
     *  path; user labels use the folder path verbatim as the label id. */
    async moveMessage(fromFolder: string, uid: number, toFolder: string, gmailId?: string): Promise<void> {
        // See trashMessage — prefer the caller's stored Gmail id; the
        // list-and-hash fallback is capped at 1000 and breaks on large
        // folders.
        let id = gmailId;
        if (!id) {
            const ids = await this.listMessageIds(`in:${this.folderToLabel(fromFolder)}`, 1000);
            id = ids.find(x => idToUid(x) === uid);
        }
        if (!id) throw new Error(`Gmail moveMessage: UID ${uid} not found in ${fromFolder}`);
        // Map the folder path to the label id. System labels are uppercased
        // aliases; user labels are passed through as-is (Gmail's label ids
        // for user-created labels match the visible label name).
        const toLabel = this.folderPathToLabelId(toFolder);
        const fromLabel = this.folderPathToLabelId(fromFolder);
        const addLabelIds: string[] = [];
        const removeLabelIds: string[] = [];
        if (toLabel) addLabelIds.push(toLabel);
        if (fromLabel && fromLabel !== toLabel) removeLabelIds.push(fromLabel);
        await this.fetch(`/messages/${id}/modify`, {
            method: "POST",
            body: JSON.stringify({ addLabelIds, removeLabelIds }),
        });
    }

    /** Folder path → Gmail label id. System folders map to uppercase label
     *  constants; anything else is treated as a user label (identical name). */
    private folderPathToLabelId(path: string): string {
        const lower = path.toLowerCase();
        if (lower === "inbox") return "INBOX";
        if (lower === "sent" || lower === "[gmail]/sent mail") return "SENT";
        if (lower === "drafts" || lower === "[gmail]/drafts") return "DRAFT";
        if (lower === "trash" || lower === "[gmail]/trash") return "TRASH";
        if (lower === "spam" || lower === "junk email" || lower === "[gmail]/spam") return "SPAM";
        if (lower === "archive" || lower === "[gmail]/all mail") return ""; // no-op — archive is absence-of-INBOX
        return path; // user label — name-as-id
    }

    /** Gmail system label ids that the API forbids renaming — labels.patch on
     *  any of these returns 400. The folder tree must refuse a rename of the
     *  folders that map to them. */
    private static readonly SYSTEM_LABELS = new Set([
        "INBOX", "SENT", "DRAFT", "TRASH", "SPAM", "CHAT",
        "IMPORTANT", "STARRED", "UNREAD",
    ]);

    /** Rename (and/or reparent) a Gmail label. Gmail has no folders — a
     *  "folder" is a label and the hierarchy is encoded in the label NAME as a
     *  slash-path ("Parent/Child"). So both rename-in-place and reparent are the
     *  same operation: PATCH the label to a new full name.
     *
     *  - `oldPath` is the current label name (== path in our folder model).
     *  - `newName` is the new LEAF name.
     *  - `newParentPath` (optional) is the destination parent label's path; when
     *    given, the new full name becomes `<newParentPath>/<newName>`, otherwise
     *    we keep the old parent prefix and just swap the leaf.
     *
     *  System labels (INBOX/SENT/…) can't be renamed — reject with a clear
     *  message the UI surfaces. We resolve the label id from the live label list
     *  rather than trusting name-as-id, because a user label's id is an opaque
     *  `Label_NN` on some accounts even though the name matches. */
    async renameFolder(oldPath: string, newName: string, newParentPath?: string): Promise<void> {
        const sysId = this.folderPathToLabelId(oldPath);
        if (GmailApiProvider.SYSTEM_LABELS.has(sysId) || sysId === "" /* All Mail */) {
            throw new Error(`"${oldPath}" is a Gmail system label and can't be renamed.`);
        }
        const data = await this.fetch("/labels");
        const labels: any[] = data.labels || [];
        const match = labels.find(l => l.name === oldPath);
        if (!match) throw new Error(`Gmail label "${oldPath}" not found`);
        if (match.type === "system") {
            throw new Error(`"${oldPath}" is a Gmail system label and can't be renamed.`);
        }
        const newFullName = (newParentPath && newParentPath.length)
            ? `${newParentPath}/${newName}`
            : (oldPath.includes("/") ? `${oldPath.slice(0, oldPath.lastIndexOf("/"))}/${newName}` : newName);
        await this.fetch(`/labels/${match.id}`, {
            method: "PATCH",
            body: JSON.stringify({ name: newFullName }),
        });
    }

    async getUids(folder: string): Promise<number[]> {
        const query = `in:${this.folderToLabel(folder)}`;
        const ids = await this.listMessageIds(query, 10000);
        const result = ids.map(idToUid);
        // Propagate the truncation flag so reconcile can refuse to delete.
        if ((ids as any)._truncated) (result as any)._truncated = true;
        return result;
    }

    async close(): Promise<void> {
        // No persistent connection to close
    }

    /** Map folder path to Gmail label query term */
    private folderToLabel(path: string): string {
        const lower = path.toLowerCase();
        if (lower === "inbox") return "inbox";
        if (lower === "sent" || lower === "[gmail]/sent mail") return "sent";
        if (lower === "drafts" || lower === "[gmail]/drafts") return "drafts";
        if (lower === "trash" || lower === "[gmail]/trash") return "trash";
        if (lower === "spam" || lower === "[gmail]/spam" || lower === "junk email") return "spam";
        if (lower === "archive" || lower === "[gmail]/all mail") return "all";
        // Custom label — use exact name
        return `"${path}"`;
    }

    /** Format date for Gmail query (YYYY/MM/DD) */
    private formatDate(d: Date): string {
        return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
    }
}
